import React from 'react';

import { useSelector } from 'react-redux';

import _get from 'lodash/get';

import { useDevice } from '@bonnet/next/device';

import {
    Alert,
    Box,
    BoxGrid,
    Image,
    InfoIcon,
    MeterBar,
    Section,
    Text,
} from 'reaxl';
import { useFeatures } from 'reaxl-features';

import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

const defaultDisclaimer = 'This battery health determination is based on information sourced by Kelley Blue Book from third parties as available for this vehicle on the generation date listed above. A battery health determination is not a guarantee of performance or safety. Kelley Blue Book has not inspected this vehicle or its battery. Any warranty or battery performance questions should be directed to the vehicle seller or authorized service provider.';
const defaultDisclaimerWithoutKbb = 'This battery health determination is based on information sourced from third parties as available for this vehicle on the generation date listed above. A battery health determination is not a guarantee of performance or safety. Neither Car and Driver nor Autotrader have inspected this vehicle or its battery. Any warranty or battery performance questions should be directed to the vehicle seller or authorized service provider.';

const averagesDisclaimer = 'This battery health determination is based on information sourced by Kelley Blue Book from third parties as available. A battery health determination is not a guarantee of performance or safety. Kelley Blue Book has not inspected this vehicle or its battery. Any warranty or battery performance questions should be directed to the vehicle seller or authorized service provider.';
const averagesDisclaimerWithoutKbb = 'This battery health determination is based on information sourced from third parties as available. A battery health determination is not a guarantee of performance or safety. Neither Car and Driver nor Autotrader have inspected this vehicle or its battery. Any warranty or battery performance questions should be directed to the vehicle seller or authorized service provider.';

const batteryHealthTooltip = {
    batteryHealth: "Battery health is the foundational truth of an EV's battery condition.",
    batteryRange: "Estimated range helps you understand how far the vehicle can travel on a full charge.As EV's age and battery health decreases, so does the overall range in the EV.",
    chargeTimeInfo: 'Charging time is an estimate based on charging from 0% to 100% battery capacity.',
};

const kbbLogoSubheading = (
    <span className="text-lg-right text-subdued-lighter">
        <Text
            size={100}
            weight="normal"
        >
            {'Sourced by '}
        </Text>
        <Text
            size={100}
            className="margin-right-2"
            weight="bold"
        >
            Kelley Blue Book
        </Text>
        <Image
            alt=""
            height={25}
            src="/cm-api/content/static/img/icon/logos/kbb-logo-notext.svg"
            width={20}
        />
    </span>
);

const generationDateSubHeading = (formattedDate) => (
    <Text
        size={200}
        weight="normal"
        componentClass="div"
        color="subdued-lightest"
    >

        {`Generated on ${formattedDate}`}
    </Text>
);

const alertSubheading = (ymmString) => (
    <Alert
        bsStyle="info"
        className="margin-top-2 text-size-200 margin-bottom-4"
    >
        The actual battery health of this vehicle may vary.
        <Text
            size="200"
            weight="normal"
        >
            {` This information represents the average condition of batteries in ${ymmString}s.`}
        </Text>
    </Alert>
);

export default function BatteryHealthContainer({
    images,
}) {
    const { battery_health: [, {
        ford_battery_health: fordBatteryHealth,
        battery_range_title: batteryRangeTitle,
        ratings_description: ratingsDescriptions,
        hide_kbb_branding: hideKbbBranding,
    }] } = useFeatures(['battery_health']);

    const device = useDevice();
    const isMd = _get(device, 'is.md', false);

    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory);
    const { electricComponentInfo, electricVehicleRange = '', make, model, year } = listing;
    const { batteryRange = '', batteryDegradationInfo = {}, chargingLevelMax = '', epaChargeTimeAt240V = '' } = electricComponentInfo;
    const { healthDescription = '', maxRange = '', minRange = '', scoreDate = '', ratingSource } = batteryDegradationInfo;

    const isRangeAvailable = minRange != null && minRange > 0 && maxRange != null && maxRange > 0;

    const ymmString = `${year} ${make?.name} ${model?.name}`;

    const isManheimAverage = ratingSource === 'Manheim Average';

    const formattedHealthDescription = healthDescription.toLowerCase();

    const convertedScoreDate = scoreDate && new Date(scoreDate);

    const formattedDate = convertedScoreDate && new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    }).format(convertedScoreDate);

    const sectionHeading = (
        <>
            <div className="display-flex flex-column flex-md-row flex-wrap justify-content-between">
                <div>
                    Understand your EV&apos;s battery health
                </div>
                {!isManheimAverage && kbbLogoSubheading}
            </div>
            {!!scoreDate && !isManheimAverage && generationDateSubHeading(formattedDate)}
            {isManheimAverage && alertSubheading(ymmString)}
        </>
    );

    const showPrimaryRange = (
        <Text
            componentClass="div"
            size={isMd ? 400 : 500}
            color="normal"
            weight="bold"
        >
            {!fordBatteryHealth && `${Math.round(minRange)} - ${Math.round(maxRange)} mi`}
            {fordBatteryHealth && `${batteryRange || electricVehicleRange} mi`}
        </Text>
    );

    const informationUnavailable = (
        <Text
            componentClass="div"
            size={200}
            color="subdued-lightest"
            weight="bold"
        >
            Information Unavailable
        </Text>
    );

    // showPrimaryRange if range is available for ATC/KBB or if fordBatteryHealth is true and batteryRange or electricVehicleRange is present
    const batteryRangePresent = (batteryRange || electricVehicleRange);
    const rangePrimaryInfo = (isRangeAvailable || (fordBatteryHealth && batteryRangePresent)) ? showPrimaryRange : informationUnavailable;

    const rangeSecondaryInfo = (!fordBatteryHealth && batteryRangePresent) && (
        <Text
            size={200}
            color="gray-dark"
        >
            {`${batteryRange || electricVehicleRange} mi Range When New`}
        </Text>
    );

    const chargeTimeInfo = epaChargeTimeAt240V ? (
        <Text
            componentClass="div"
            weight="bold"
            size={isMd ? 400 : 500}
        >
            {`${epaChargeTimeAt240V} hr`}
        </Text>
    ) : informationUnavailable;

    const chargeLevelInfo = chargingLevelMax && (
        <Text
            size={200}
            color="gray-dark"
        >
            {chargingLevelMax}
        </Text>
    );

    const healthDescriptionText = (
        <Text
            className="text-capitalize"
            componentClass="div"
            weight="bold"
            size={isMd ? 400 : 500}
        >
            {`${formattedHealthDescription}`}
        </Text>
    );

    const batteryHealthInfoBox = ({ image = {}, title, primaryInfo, secondaryInfo, tooltip }) => {
        if (isManheimAverage && !fordBatteryHealth) {
            title = `Avg. ${title}`;
        }

        return (
            <Box
                className="display-flex flex-row gap-3 align-items-center padding-left-3 position-relative"
                backgroundColor="transparent"
                borderColor="neutral-secondary"
            >
                <div>
                    {image && (
                        <Image
                            alt={image.alt}
                            height={48}
                            src={image.src}
                            width={48}
                        />
                    )}
                </div>
                <div className="display-flex flex-column align-self-baseline">
                    <div>
                        <Text>
                            {title}
                        </Text>
                        <InfoIcon
                            className="margin-left-1"
                            infoText={tooltip}
                            placement="top"
                        />
                    </div>
                    { primaryInfo }
                    { secondaryInfo }
                </div>
            </Box>
        );
    };

    let disclaimer;
    if (isManheimAverage) {
        disclaimer = hideKbbBranding ? averagesDisclaimerWithoutKbb : averagesDisclaimer;
    } else {
        disclaimer = hideKbbBranding ? defaultDisclaimerWithoutKbb : defaultDisclaimer;
    }

    return (
        <Section
            alignment="left"
            data-cmp="batteryHealthSection"
            heading={sectionHeading}
            id="batteryHealthSection"
            uiContext="level-3"
            className="padding-vertical-4 padding-vertical-md-5"
        >
            <BoxGrid
                className="padding-bottom-4"
                columnCount={3}
            >
                {batteryHealthInfoBox({
                    image: images.batteryHealth,
                    title: 'Battery Health',
                    primaryInfo: healthDescriptionText,
                    secondaryInfo: (
                        <MeterBar
                            className="margin-top-2"
                            barCount={5}
                            value={formattedHealthDescription}
                            theme="status"
                        />),
                    tooltip: batteryHealthTooltip.batteryHealth,
                })}

                {batteryHealthInfoBox({
                    image: images.batteryRange,
                    title: batteryRangeTitle,
                    primaryInfo: rangePrimaryInfo,
                    secondaryInfo: rangeSecondaryInfo,
                    tooltip: batteryHealthTooltip.batteryRange,
                })}

                {batteryHealthInfoBox({ image: images.electricIcon,
                    title: 'Charging Time',
                    primaryInfo: chargeTimeInfo,
                    secondaryInfo: chargeLevelInfo,
                    tooltip: batteryHealthTooltip.chargeTimeInfo,
                })}
            </BoxGrid>

            <div className="row padding-horizontal-4">
                <Text
                    className="text-capitalize"
                    componentClass="span"
                    weight="bold"
                    size="200"
                >
                    {`${formattedHealthDescription}: `}
                </Text>
                <Text
                    componentClass="span"
                    size="200"
                >
                    {ratingsDescriptions[formattedHealthDescription]}
                </Text>
                <Text
                    className="margin-top-2"
                    color="subdued-lightest"
                    size="100"
                >
                    {disclaimer}
                </Text>
            </div>
        </Section>
    );
}
